import React, { useState } from 'react';
import { Box, CardContent, Typography, IconButton, Paper, TextField, TextareaAutosize, Button, Grid } from '@material-ui/core';
import { Add, Save, Clear } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { useMutation, useQuery } from '@apollo/client';
import client from '../../lib/apolloClient';
import gql from 'graphql-tag';
import { get } from 'lodash';
import { S3Image } from '../../lib/s3';
import { connect } from 'react-redux';

const CREATE_TEAM_REQUEST = gql`
  mutation CreateTeamRequest($emails: [String!]!, $imageName: String!, $teamName: String, $teamHeadline: String, $teamDescription: String) {
    createTeamRequest(emails: $emails, imageName: $imageName, teamName: $teamName, teamHeadline: $teamHeadline, teamDescription: $teamDescription) {
      id
      status
      __typename
    }
  }
`;

const getCurrentUser = gql`
  query getCurrentUser {
    getCurrentUser {
      id
      company {
        id
        name
        affiliate_name
        team_name_approval_url
      }
      inventory_user {
        id
        catestrophic_plan_on_file
        continuity_builder {
          id
        }
      }
    }
    mySuccessionPlans {
      id
      status
      planTemplate {
        id
        slug
        planType
      }
    }
  }
`;

const GENERATE_TEAM_BIO = gql`
  mutation GenerateTeamBio($emails: [String!]) {
    generateTeamBio(emails: $emails)
  }
`;

const TeamRequestForm = ({ user, classes, company }) => {
  const [emails, setEmails] = useState(['']);
  const [creating, setCreating] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamHeadline, setTeamHeadline] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionWordCount, setDescriptionWordCount] = useState(0);
  const [createTeamRequest] = useMutation(CREATE_TEAM_REQUEST, { client });
  const [generateTeamBio] = useMutation(GENERATE_TEAM_BIO, {
    client,
    onCompleted: (data) => {
      setTeamDescription(data.generateTeamBio);
      setIsGeneratingDescription(false);
    },
    onError: (error) => {
      console.error('Error generating team bio:', error);
      setIsGeneratingDescription(false);
    }
  });
  const showTeamAgreementsRequiredCopy = get(company, 'features', []).includes('team_agreements_required');
  const { data: userData, loading: userDataLoading } = useQuery(getCurrentUser, { client });
  const teamNameApprovalUrl = get(userData, 'getCurrentUser.company.team_name_approval_url');
  let companyName = get(userData, 'getCurrentUser.company.affiliate_name', '');
  companyName = companyName ? companyName.charAt(0).toUpperCase() + companyName.slice(1) : '';
  const teamNameRequiresApproval = get(company, 'features', []).includes('team_name_requires_approval');
  
  // Check if catastrophic plan requirement is bypassed by feature flag
  const skipCatPlanCheck = get(company, 'features', []).includes('team_requests_only_with_cat_plan');
  
  // Check if user has catastrophic plan via inventory_user flag OR succession plans OR continuity builder
  const catPlanOnFile = get(userData, 'getCurrentUser.inventory_user.catestrophic_plan_on_file');
  const continuityBuilder = get(userData, 'getCurrentUser.inventory_user.continuity_builder');
  const myPlans = get(userData, 'mySuccessionPlans', []);
  
  // Check if user has a catastrophic plan in progress or completed
  const hasCatastrophicPlanInProgress = myPlans.some((plan) => {
    const planType = get(plan, 'planTemplate.planType', '');
    const planSlug = get(plan, 'planTemplate.slug', '');
    const status = get(plan, 'status', '');
    
    const isCatastrophicPlan = planType === 'catastrophic' || planSlug === 'catastrophic-plan-v1';
    const isStartedOrCompleted = status === 'in_progress' || status === 'completed';
    
    return isCatastrophicPlan && isStartedOrCompleted;
  });
  
  // Check legacy continuity builder (catastrophic plan system)
  const hasLegacyCatastrophicPlan = continuityBuilder && continuityBuilder.id;
  
  // User has catastrophic plan if:
  // 1. The flag is true, OR
  // 2. They have one in the new succession planning system, OR
  // 3. They have a legacy continuity builder record
  const hasCatastrophicPlan = catPlanOnFile === true || hasCatastrophicPlanInProgress || hasLegacyCatastrophicPlan;
  const needsCatastrophicPlan = !skipCatPlanCheck && !hasCatastrophicPlan;

  React.useEffect(() => {
    const words = teamDescription.trim().split(/\s+/).filter(word => word.length > 0);
    setDescriptionWordCount(words.length);
  }, [teamDescription]);

  const handleGenerateDescription = () => {
    setIsGeneratingDescription(true);
    // Filter out empty emails and pass valid ones to the mutation
    const validEmails = emails.filter(email => email && email.match(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/));
    generateTeamBio({ variables: { emails: validEmails } });
  };

  const handleCreateTeamRequest = async () => {
    setCreating(true);
    createTeamRequest({ variables: { emails, imageName: selectedImage, teamName, teamHeadline, teamDescription } }).then(result => {
      setEmails(['']);
      setSelectedImage('');
      setCreating(false);
      window.location.href = '/team_requests';
    });
  };
  
  const allEmailsFilledOut = emails.every(email => {
    return Boolean(email.match(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/));
  });

  const imageOptions = ['1', '2', '3', '4', '5', '6'];

  return (
    <Box className={classes.root}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h5" gutterBottom>
          Invite Team Members
        </Typography>
        {showTeamAgreementsRequiredCopy ? (
          <Typography variant="body1" gutterBottom>
            On a team? Want to highlight the strength of your practice? Great, Simply enter the emails of the other advisors on your team.
            Your request will be submitted to the home office and when approved your profile will be updated to reflect that of your team.
            <br />
            <strong>NOTE: You must have an approved Team Agreement and Catastrophic Plan on file to have your profile converted to a team.</strong>
          </Typography>
        ) : (
          <Typography variant="body1" gutterBottom>
            On a team? Want to highlight the strength of your practice? Great, Simply enter the emails of the other advisors on your team.
            Your request will be submitted to the home office and when approved your profile will be updated to reflect that of your team.
          </Typography>
        )}
        <Paper className={classes.emailBox}>
          <Typography variant="h6" gutterBottom>
            {teamNameRequiresApproval 
              ? `${companyName} Approved Team Name`
              : 'Give your team a name'}
          </Typography>
          <TextField
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            label="Team Name"
            variant="outlined"
            fullWidth
          />
          {teamNameRequiresApproval && (
            <Typography variant="body2"> 
            Please note: You must supply an approved {companyName} Team Name and Catastrophic Plan on file to have your profile converted to a team. If you do not have an approved {companyName} Team Name you must complete the{' '}
            <a href={teamNameApprovalUrl} target="_blank" rel="noopener noreferrer">
              Approval Form here
            </a>
            .
          </Typography>
          )}
        </Paper>
        <Paper className={classes.emailBox}>
          <Typography variant="h6" gutterBottom>
            Enter the emails of your team members
          </Typography>
          {emails.map((email, index) => (
            <Box key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <TextField
                value={email}
                className={classes.emailInput}
                onChange={(e) => setEmails([...emails.slice(0, index), e.target.value, ...emails.slice(index + 1)])}
                label={`Email #${index + 1}`} 
                variant="outlined"
                size="small"
              />
              {index !== 0 && (
                <IconButton 
                  onClick={() => setEmails([...emails.slice(0, index), ...emails.slice(index + 1)])}
                  style={{ 
                    color: '#f44336',
                    padding: '8px'
                  }}
                  size="small"
                >
                  <Clear />
                </IconButton>
              )}
            </Box>
          ))}
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Add />}
            onClick={() => setEmails([...emails, ''])}
            style={{ marginTop: '8px' }}
          >
            Add Email
          </Button>
        </Paper>
        <Paper className={classes.emailBox}>
          <Typography variant="h6" gutterBottom>
            Add a team headline
          </Typography>
          <TextField
            value={teamHeadline}
            onChange={(e) => setTeamHeadline(e.target.value)}
            label="Team Headline"
            variant="outlined"
            fullWidth
          />
          <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
            Add a team description
          </Typography>
          <Typography variant="body2" className={classes.helperText}>
            Describe your team's strengths, specialties, and what makes you unique. 
            Include your collective experience, areas of expertise, and the value you bring to clients.
            Aim for 2-3 paragraphs that highlight your team's collaborative approach.
          </Typography>
          <TextareaAutosize
            value={teamDescription}
            placeholder="Example: Our team combines over 50 years of experience in wealth management, estate planning, and retirement strategies. We specialize in serving high-net-worth individuals and families, providing comprehensive financial guidance..."
            minRows={6}
            spellCheck={true}
            key="bio"
            style={{ width: '100%', padding: '8px', fontSize: '14px', fontFamily: 'inherit', border: '1px solid #ccc', borderRadius: '4px' }}
            onChange={(e) => setTeamDescription(e.target.value)}
          />
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <Typography variant="caption" className={classes.wordCount}>
              {descriptionWordCount} words
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<i className="fa-solid fa-robot"></i>}
              onClick={handleGenerateDescription}
              disabled={isGeneratingDescription}
              size="small"
            >
              {isGeneratingDescription ? 'Generating...' : 'Generate with AI'}
            </Button>
          </Box>
          <Box mt={2} p={2} style={{ 
            backgroundColor: '#f5f5f5', 
            borderLeft: '4px solid #ff9800',
            borderRadius: '4px'
          }}>
            <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              AI Privacy & Security
            </Typography>
            <Typography variant="body2" style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
              Our AI features are designed with privacy, security, and fairness in mind. 
              Client data is never used to train external AI models, and all use of AI 
              complies with applicable privacy, confidentiality, and information security 
              requirements. Please always review the details for accuracy and suitability 
              as outputs may contain mistakes.
            </Typography>
          </Box>
        </Paper>
        <Paper className={classes.imageBox}>
          <Typography variant="h6" gutterBottom>
            Select Team Image
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {imageOptions.map((option) => (
              <Grid item key={option}>
                <Box
                  className={`${classes.imageOption} ${selectedImage === option ? classes.selectedImage : ''}`}
                  onClick={() => setSelectedImage(option)}
                >
                  <img 
                    src={S3Image(`${company?.short_name}-${option}-team.png`)}
                    alt={`Image ${option}`}
                    className={classes.teamImage} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
        <Box className={classes.box}>
          <center>
            <Box className={classes.warningBox}>
              <Typography variant="body2" className={classes.warningText}>
                <strong>Important:</strong> Your team request will not be approved until all invited 
                team members have accepted or denied the invitation. Please ensure all team members 
                check their email and respond to the team request.
              </Typography>
            </Box>
            {needsCatastrophicPlan && (
              <Box className={classes.errorBox}>
                <Typography variant="body2" className={classes.errorText}>
                  Before you can create a team, you must have a catastrophic plan on file (can be pending approval).
                  <br />
                  <a href="/catastrophic_planning" style={{ color: '#721c24', fontWeight: 'bold', textDecoration: 'underline' }}>
                    Click here to create a catastrophic plan.
                  </a>
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              onClick={handleCreateTeamRequest}
              disabled={!allEmailsFilledOut || creating || !selectedImage || needsCatastrophicPlan}
            >
              <Save /> &nbsp; Submit Team Request
            </Button>
            <hr className={classes.line} />
            <Typography variant="body2" gutterBottom className={classes.disclaimer}>
              By clicking "Submit Team Request" your request will be forwarded to the home office and you will be notified shortly after.
            </Typography>
          </center>
        </Box>
      </CardContent>
    </Box>
  );
}

const styles = theme => ({
  root: {
    padding: theme.spacing(2),
  },
  cardContent: {
    minHeight: 600,
    padding: theme.spacing(2),
  },
  messageBox: {
    display: 'flex',
    flexDirection: 'column',
  },
  emailBox: {
    padding: theme.spacing(2),
    width: 800,
    margin: 'auto',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  imageBox: {
    padding: theme.spacing(2),
    width: 800,
    margin: 'auto',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  emailInput: {
    flex: 1,
  },
  box: {
    marginTop: theme.spacing(1),
  },
  line: {
    width: 400,
  },
  disclaimer: {
    color: '#999',
    width: 400,
  },
  imageOption: {
    cursor: 'pointer',
    border: '2px solid transparent',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    transition: 'border-color 0.3s',
  },
  selectedImage: {
    borderColor: theme.palette.primary.main,
  },
  teamImage: {
    width: 80,
    height: 80,
    borderRadius: theme.shape.borderRadius,
  },
  helperText: {
    color: '#666',
    fontSize: '13px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    lineHeight: '1.6',
  },
  wordCount: {
    color: '#999',
    fontSize: '12px',
  },
  warningBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxWidth: 650,
  },
  warningText: {
    color: '#856404',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '4px',
    padding: theme.spacing(2),
    fontSize: '14px',
    lineHeight: '1.5',
    textAlign: 'left',
  },
  errorBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxWidth: 650,
  },
  errorText: {
    color: '#721c24',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: theme.spacing(2),
    fontSize: '14px',
    lineHeight: '1.5',
    textAlign: 'left',
  },
});

const mapStateToProps = state => ({
  company: state.company,
});

export default connect(mapStateToProps)(withStyles(styles)(TeamRequestForm));